import React from 'react';
import moment from 'moment';
import Model from 'core/model/Model';
import { formatDate } from 'core/util/dateUtils';
import $devices from 'app/stores/device/$devices';
import $kproxyAgents from 'app/stores/kproxyAgent/$kproxyAgents';
import { KPROXY_VERSION_MAP } from '@kentik/ui-shared/agents/constants';
import { isLegacyKproxyBuild } from '@kentik/ui-shared/agents/utils';

export default class KproxyAgentModel extends Model {
  constructor(attributes) {
    super(attributes || {});

    if (attributes && !attributes.user_id) {
      $kproxyAgents.fetchKproxyUser().then((user) => {
        this.set('user_id', user.id);
      });
    }
  }

  get urlRoot() {
    return '/api/ui/kproxyAgent';
  }

  get defaults() {
    return {
      agent_version: '',
      devices: []
    };
  }

  defaultName = 'Auto Generated';

  get messages() {
    return {
      create: `kproxy Agent ${this.get('agent_name')} was updated successfully`,
      update: `kproxy Agent ${this.get('agent_name')} was updated successfully`,
      destroy: `kproxy Agent ${this.get('agent_name')} was removed successfully`
    };
  }

  get removalConfirmText() {
    return {
      title: 'Remove kproxy',
      text: (
        <p>
          Are you sure you want to remove <strong>{this.get('agent_name')}?</strong> Note: This only removes the
          registration and not the kproxy itself.
        </p>
      )
    };
  }

  get siteName() {
    const site = this.get('site');
    return site ? site.title : '';
  }

  get deviceNames() {
    const devices = this.get('devices') || [];
    return devices.map((device) => device.device_name);
  }

  get agentUserName() {
    const agentUser = this.get('agentUser');
    return agentUser ? agentUser.user_full_name : 'Unknown';
  }

  get agentUserEmail() {
    const agentUser = this.get('agentUser');
    return agentUser ? agentUser.user_email : 'Unknown';
  }

  get needsUpdate() {
    return !this.get('agent_private_ip') || !this.get('site_id') || this.get('agent_name') === this.defaultName;
  }

  getVersionInfo(agent_version, agent_built_on) {
    if (agent_version && agent_built_on) {
      const entry = KPROXY_VERSION_MAP[agent_version];
      return (entry && entry.version) || `${agent_version.substr(0, 7)} ${formatDate(agent_built_on, 'YYYY-MMM')}`;
    }

    return 'Unknown';
  }

  get isRunningLegacyVersion() {
    return isLegacyKproxyBuild({
      agent_built_on: this.get('agent_built_on'),
      agent_version: this.get('agent_version')
    });
  }

  getHealth(last_access, agent_ip) {
    const minutesAgo = moment.utc().diff(last_access, 'minutes');

    if (!agent_ip) {
      return 'Pending';
    }

    if (minutesAgo <= 30) {
      return 'Healthy';
    }

    if (minutesAgo <= 45) {
      return 'Warning';
    }

    return 'Critical';
  }

  deserialize(data = {}) {
    const { agent_ip, agent_private_ip, agent_built_on, last_access, site_id } = data;
    let { agent_name, agent_version, devices } = data;

    agent_name = agent_name || this.defaultName;
    agent_version = agent_version || '';
    devices = devices || [];

    const version = this.getVersionInfo(agent_version, agent_built_on);
    const health = this.getHealth(last_access, agent_ip);
    const title = `${agent_name} ${agent_ip || ''}${agent_private_ip ? ` (${agent_private_ip})` : ''}${
      version === 'Unknown' ? '' : ` v${version}`
    }`;

    return {
      ...data,
      agent_name,
      agent_version,
      devices: $devices.deviceSummaries.filter((d) => devices.includes(d.id)),
      site_id: parseInt(site_id, 10),
      title,
      version,
      health
    };
  }
}
